import { gql } from "urql";
import type { TypedDocumentNode } from "urql";
import { useLocale } from "@acto/web-common/locales";
import { useComparisonModeContext, useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { Kpi as GqlKpi, type KpiComparisonMode as GqlKpiComparisonMode, ProductTrendFilter } from "@acto/gql/graphql";
import { Kpi } from "@acto/taskpane/core/domain";
import { useNumberFormatter } from "@acto/taskpane/core/utils/hooks/useNumberFormatter.tsx";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import { toGqlKpiComparisonMode } from "@acto/taskpane/core/data-access/customers/kpiTransformers";
import {
  type ProductGroupNode,
  toOrderKpi,
  toProductValueItem,
} from "@acto/taskpane/field-sales-2/data-access/productTrendItem";
import type { ProductGroups } from "@acto/taskpane/field-sales-2/data-access/useProductGroups";
import { type CorporateGroupEnvelope, firstCorporateGroup } from "./corporateGroupEnvelope";

const PAGE_SIZE = 5;

type GroupProductGroupsQueryData = CorporateGroupEnvelope<{
  id: string;
  products: {
    edges: { node: ProductGroupNode }[];
    pageInfo: { hasNextPage: boolean; endCursor: string | null };
  };
}>;

interface GroupProductGroupsQueryVariables {
  clientId: string;
  kpis: GqlKpi[];
  parent?: string | null;
  first: number;
  after?: string | null;
  dateRange?: { startDate: string; endDate: string };
  searchTerm?: string | null;
  trends?: ProductTrendFilter[] | null;
  orderKpi?: GqlKpi | null;
  comparisonMode: GqlKpiComparisonMode;
}

const GroupProductGroupsQuery: TypedDocumentNode<GroupProductGroupsQueryData, GroupProductGroupsQueryVariables> = gql`
  query GetCorporateGroupProductGroups(
    $clientId: ID!
    $kpis: [Kpi!]
    $parent: ID
    $first: Int!
    $after: String
    $dateRange: DateRangeInput
    $searchTerm: String
    $trends: [ProductTrendFilter!]
    $orderKpi: Kpi
    $comparisonMode: KpiComparisonMode!
  ) {
    clients(first: 1, filter: { id: $clientId }) {
      edges {
        node {
          id
          corporateGroup {
            id
            products(
              kpis: $kpis
              parent: $parent
              first: $first
              after: $after
              dateRange: $dateRange
              searchTerm: $searchTerm
              trends: $trends
              orderKpi: $orderKpi
              comparisonMode: $comparisonMode
            ) {
              edges {
                node {
                  id
                  productGroup
                  externalReference
                  hasChildren
                  values {
                    kpi
                    value
                    change {
                      percentageChange
                    }
                  }
                }
              }
              pageInfo {
                hasNextPage
                endCursor
              }
            }
          }
        }
      }
    }
  }
`;

const EMPTY_CONNECTION = {
  edges: [],
  pageInfo: { hasNextPage: false, endCursor: null },
};

export interface GroupProductGroupsOptions {
  clientId: string | undefined;
  parent?: string;
  dateRange?: { startDate: string; endDate: string } | null;
  searchTerm?: string;
  selectedKpi?: Kpi;
  trends?: ProductTrendFilter[];
  enabled?: boolean;
}

/** The corporate group's drillable product breakdown (all members), keyed off one member client. */
export function useGroupProductGroups({
  clientId,
  parent,
  dateRange,
  searchTerm,
  selectedKpi = Kpi.REVENUE,
  trends,
  enabled = true,
}: GroupProductGroupsOptions): ProductGroups {
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const formatNumber = useNumberFormatter();
  const comparisonMode = toGqlKpiComparisonMode(useComparisonModeContext());

  const trimmedSearchTerm = searchTerm?.trim() ?? "";
  const activeSearchTerm = trimmedSearchTerm.length > 0 ? trimmedSearchTerm : null;
  const activeTrends = trends && trends.length > 0 ? trends : undefined;
  const orderKpi = toOrderKpi(selectedKpi);

  const variables = {
    clientId: clientId ?? "",
    comparisonMode,
    kpis: [GqlKpi.Revenue, GqlKpi.Profit, GqlKpi.Margin, GqlKpi.Orders],
    parent: parent ?? null,
    dateRange: dateRange ?? undefined,
    searchTerm: activeSearchTerm,
    trends: activeTrends,
    orderKpi,
  };

  const { data, isFetching, hasNextPage, fetchNextPage, error } = usePagination<
    GroupProductGroupsQueryData,
    GroupProductGroupsQueryVariables,
    ProductGroupNode
  >({
    query: GroupProductGroupsQuery,
    limit: PAGE_SIZE,
    enabled: enabled && !!clientId,
    variables,
    connection: (queryData) => firstCorporateGroup(queryData)?.products ?? EMPTY_CONNECTION,
  });

  const items = (data ?? []).map((node) => toProductValueItem(node, selectedKpi, locale, currency, formatNumber));

  // First-page (re)load including stale revalidation: jumping to another group resets the list to []
  // while urql serves a lingering partial cache hit (fetching false, stale true), so gate on the
  // accumulated list. usePagination's `isPending` reads urql's raw data and stays false through it.
  const isFirstPageLoading = isFetching && items.length === 0;

  return {
    isPending: isFirstPageLoading,
    isEmpty: !isFirstPageLoading && !error && items.length === 0,
    error,
    items,
    hasMore: hasNextPage,
    isLoadingMore: isFetching && items.length > 0,
    loadMore: fetchNextPage,
    isSearchActive: activeSearchTerm != null,
  };
}
